import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  InlineStack,
  Strong,
  TableTd,
  TableTr,
  Text,
} from '@customerio/pluma-components/react';
import { PlatformIcon, type PushPlatform } from '@/features/push-credentials';
import { ViewerRestrictedTooltip } from '@/components/overlays/viewer-restricted-tooltip';
import { VIEWER_RESTRICTED_MESSAGE } from '@/utils/viewer-restricted';
import type { PlatformRow } from '../hooks/use-platform-drafts';

export type PlatformTableRowProps = {
  row: PlatformRow;
  canSaveForm: boolean;
  onEnable: (platform: PushPlatform) => void;
  onEdit: (platform: PushPlatform) => void;
  onDisable: (platform: PushPlatform) => void;
};

export function PlatformTableRow({ row, canSaveForm, onEnable, onEdit, onDisable }: PlatformTableRowProps) {
  return (
    <TableTr>
      <TableTd whiteSpace="nowrap">
        <InlineStack gap="050" alignBlock="center">
          {/* Decorative: the row names its platform in text right beside it. */}
          <PlatformIcon platform={row.platform} />
          <Strong>{row.label}</Strong>
        </InlineStack>
      </TableTd>
      <TableTd w="full">{row.identifier === '' ? null : <Text truncate={true}>{row.identifier}</Text>}</TableTd>
      <TableTd whiteSpace="nowrap">
        <InlineStack gap="050" alignBlock="center" alignInline="end">
          <Badge color={row.statusColor} whiteSpace="nowrap">
            {row.statusLabel}
          </Badge>
          {row.isPending ? <Badge color="caution">Unsaved</Badge> : null}
        </InlineStack>
      </TableTd>
      <TableTd whiteSpace="nowrap" textAlign="right">
        {row.isEnabled ? (
          <DropdownMenu placement="bottom-end">
            <DropdownMenuTriggerButton
              variant="tertiary"
              size="sm"
              icon="more"
              isIconOnly={true}
              isDisabled={row.isMenuReadOnly}
              aria-label={`${row.label} actions`}
            />
            <DropdownMenuContent>
              <DropdownMenuItem
                icon="edit"
                isDisabled={row.isEditReadOnly}
                tooltip={row.isEditReadOnly ? VIEWER_RESTRICTED_MESSAGE : undefined}
                onClick={() => onEdit(row.platform)}
              >
                Edit…
              </DropdownMenuItem>
              <DropdownMenuItem
                icon="stop-circle"
                isCritical={true}
                isDisabled={row.isDisableReadOnly}
                tooltip={row.isDisableReadOnly ? VIEWER_RESTRICTED_MESSAGE : undefined}
                onClick={() => onDisable(row.platform)}
              >
                Disable
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <ViewerRestrictedTooltip readOnly={!canSaveForm}>
            {(context) => (
              <Button
                ref={context.reference.setReference}
                {...context.reference.getReferenceProps()}
                variant="tertiary"
                size="sm"
                isDisabled={!canSaveForm}
                onClick={() => onEnable(row.platform)}
              >
                Enable…
              </Button>
            )}
          </ViewerRestrictedTooltip>
        )}
      </TableTd>
    </TableTr>
  );
}
